// /login — passwordless email sign-in.
//
// Auto-mounted at /login by the pages routing convention (src/pages/<name>.tsx).
// Preserves ?next= across the magic-link round trip so a joiner who arrived at
// /join?code=XYZ lands back on /join?code=XYZ with a session.

import { useEffect, useState, type CSSProperties, type FormEvent, type JSX } from 'react'
import { supabase } from '../lib/supabaseClient'
import { AuthProvider, authErrorFromHash, useAuth } from '../lib/auth'

/**
 * Only ever redirect to a path on this origin. `?next=` comes off the URL, so
 * "//evil.example" or "https://evil.example" must not survive as a redirect target.
 */
function safeNext(raw: string | null): string {
  if (!raw) return '/'
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/'
  return raw
}

const styles: Record<string, CSSProperties> = {
  shell: {
    maxWidth: '34rem',
    margin: '0 auto',
    padding: '2.5rem 1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  label: { fontWeight: 600 },
  input: {
    width: '100%',
    minHeight: '3rem',
    padding: '0.75rem',
    fontSize: '1rem',
    borderRadius: '0.5rem',
    border: '1px solid var(--muted)',
    background: 'transparent',
    color: 'inherit',
  },
  button: {
    width: '100%',
    minHeight: '3rem',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: '0.5rem',
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    cursor: 'pointer',
  },
  linkButton: {
    minHeight: '3rem',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    borderRadius: '0.5rem',
    border: '1px solid var(--accent)',
    background: 'transparent',
    color: 'var(--accent)',
    cursor: 'pointer',
  },
  muted: { color: 'var(--muted)' },
  error: { color: '#b3261e', fontWeight: 600 },
}

function LoginInner(): JSX.Element {
  const { session, loading: authLoading } = useAuth()
  const [next, setNext] = useState('/')
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setNext(safeNext(params.get('next')))
    // A dead link either lands here directly (hash) or is handed over by
    // RequireAuth as ?auth_error=.
    setError(params.get('auth_error') ?? authErrorFromHash())
  }, [])

  // Already signed in — nothing to do here, go where they were headed.
  useEffect(() => {
    if (authLoading || !session) return
    window.location.replace(next)
  }, [authLoading, session, next])

  async function sendLink(e: FormEvent): Promise<void> {
    e.preventDefault()
    const address = email.trim()
    if (!address) return

    setSending(true)
    setError(null)
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: address,
      options: { emailRedirectTo: `${window.location.origin}${next}` },
    })
    setSending(false)

    if (otpError) {
      setError(otpError.message)
      return
    }
    setSent(true)
  }

  if (sent) {
    return (
      <main style={styles.shell}>
        <h1>Check your email</h1>
        <p>
          A sign-in link is on its way to <strong>{email.trim()}</strong>. Open it on this phone and
          you&rsquo;ll come straight back here.
        </p>
        <p style={styles.muted}>It can take a minute. Check spam before asking anyone.</p>
        <button
          type="button"
          style={styles.linkButton}
          onClick={() => {
            setSent(false)
            setError(null)
          }}
        >
          Use a different email
        </button>
      </main>
    )
  }

  return (
    <main style={styles.shell}>
      <h1>Sign in</h1>
      <p style={styles.muted}>
        No password. Enter your email and we&rsquo;ll send you a link that signs you in.
      </p>

      {error ? (
        <p style={styles.error} role="alert">
          {error} — request a new link below.
        </p>
      ) : null}

      <form onSubmit={sendLink} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <label htmlFor="email" style={styles.label}>
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          style={styles.input}
        />
        <button type="submit" style={styles.button} disabled={sending}>
          {sending ? 'Sending…' : 'Email me a sign-in link'}
        </button>
      </form>
    </main>
  )
}

export default function LoginPage(): JSX.Element {
  // Self-wrapped so the page works before spz-00 mounts a root AuthProvider.
  // AuthProvider is nest-safe, so this becomes a no-op once the root one lands.
  return (
    <AuthProvider>
      <LoginInner />
    </AuthProvider>
  )
}
